import * as THREE from 'three';
import { fetchProperty, roomsOf } from './data/property.js';
import { MAP, RENDER, WALK } from './config.js';
import { createStage } from './viewer/createStage.js';
import { loadSplat } from './viewer/loadSplat.js';
import { loadModel } from './viewer/loadModel.js';
import { loadHouse } from './viewer/loadHouse.js';
import { createCameraRig } from './viewer/cameraRig.js';
import { createMeasureTool } from './viewer/measure.js';
import { createLoadingOverlay } from './ui/loadingOverlay.js';
import { createViewpointBar } from './ui/viewpointBar.js';
import { createToolbar } from './ui/toolbar.js';
import { createAiPanel } from './ui/aiPanel.js';
import { createAuthorPanel } from './ui/authorPanel.js';
import { createFloorPlan } from './ui/floorPlan.js';
import { createJoystick } from './ui/joystick.js';
import { createZoomControl, distanceScaleFor, fovFor } from './ui/zoomControl.js';
import { createPropertySwitcher, fetchPropertyList } from './ui/propertySwitcher.js';
import { createPhotoViewer } from './ui/photoViewer.js';
import { createAvatar } from './viewer/avatar.js';
import { createWalkMode } from './viewer/walkMode.js';
import { buildNavGrid } from './viewer/navGrid.js';
import { buildLayeredNav } from './viewer/navLayers.js';
import { createSimpleMap } from './viewer/simpleMap.js';
import { measureRooms } from './viewer/house/roomArea.js';
import { createMapCamera, mapHeightFor } from './viewer/mapCamera.js';
import { createDisplayModeSwitch } from './ui/displayModeSwitch.js';
import { createFurnitureEditor } from './ui/furnitureEditor.js';
import { createFurnitureRemover } from './viewer/furnitureRemover.js';
import { createPriceTags } from './ui/priceTags.js';
import { quoteFor } from './data/quotes/index.js';
import { createPropertyIndex } from './ui/propertyIndex.js';
import { indexPath, propertyPath, readPropertyLocation } from './data/url.js';

async function main() {
  const container = document.getElementById('app');
  if (container === null) throw new Error('#app が見つかりません');

  const overlay = createLoadingOverlay(container);

  try {
    const { id: propertyId, legacy } = readPropertyLocation(window.location);
    // 古い形式（/?property=gk702）で開かれたら、新しい形式（/gk702）に書き換える
    if (legacy && propertyId !== null) {
      window.history.replaceState(null, '', propertyPath(propertyId, window.location.search));
    }

    const propertyList = await fetchPropertyList();

    if (propertyId === null) {
      // トップページ。どの物件を見るか選んでもらう
      document.title = 'AI内見';
      overlay.hide();
      createPropertyIndex(container, { properties: propertyList, search: window.location.search });
      return;
    }

    const property = await fetchProperty(propertyId);
    document.title = `${property.name}｜AI内見`;

    if (property.splat === null && property.model === null && property.plan === null) {
      // 3Dスキャンがまだ無い物件は写真で内見する
      overlay.setMessage('写真を読み込んでいます…');
      startPhotoTour({ container, property, propertyList });
    } else {
      overlay.setMessage('スキャンデータを読み込んでいます…');
      const stage = createStage(container);
      const space = await loadSpace(stage.scene, property, (ratio) => overlay.setProgress(ratio));
      startViewer({ container, stage, space, property, propertyList });
    }

    overlay.hide();
  } catch (error) {
    // 物件IDが違う・消えたときでも、一覧から選び直せるようにする
    overlay.fail(error, { link: { href: indexPath(window.location.search), label: '物件一覧へ' } });
  }
}

/** 表示する空間を読み込む。メッシュがあればメッシュ（当たり判定が正確で、撮影範囲外も崩れにくい） */
async function loadSpace(scene, property, onProgress) {
  if (property.plan !== null) return loadHouse(scene, property, { onProgress });
  if (property.model !== null) return loadModel(scene, property, { onProgress });
  return loadSplat(scene, property, { onProgress });
}

/**
 * 写真での内見。
 * 3Dスキャンが無い物件でも、間取り図とAI内見はそのまま使える。
 */
function startPhotoTour({ container, property, propertyList }) {
  container.classList.add('is-photo-mode');

  const rooms = roomsOf(property);
  if (rooms.length === 0) throw new Error('表示できる写真がありません');

  const setAiVisible = (visible) => {
    ai.setVisible(visible);
    container.classList.toggle('is-ai-open', visible);
  };

  const ai = createAiPanel(container, property, {
    onClose: () => {
      setAiVisible(false);
      toolbar.setAiActive(false);
    },
  });

  const toolbar = createPhotoToolbar(container, { onToggleAi: setAiVisible });

  createPropertySwitcher(container, {
    properties: propertyList,
    currentId: property.id,
    onSelect: (id) => {
      window.location.assign(propertyPath(id, window.location.search));
    },
  });

  const photos = createPhotoViewer(container, { property });

  const goToRoom = (name, index) => {
    bar.select(index);
    photos.showRoom(name);
    ai.setRoom(name);
    floorPlan?.setActive(name);
  };

  // 部屋ボタンは3Dのときと同じ見た目にするため、視点と同じ形に合わせる
  const bar = createViewpointBar(
    container,
    rooms.map((name) => ({ name })),
    (roomLike, index) => goToRoom(roomLike.name, index),
  );

  const floorPlan =
    property.floorplan === null
      ? null
      : createFloorPlan(container, {
          floorplan: property.floorplan,
          viewpoints: rooms.map((name) => ({ name })),
          onSelect: (roomLike) => goToRoom(roomLike.name, rooms.indexOf(roomLike.name)),
          collapsed: true,
        });

  goToRoom(rooms[0], 0);
}

/** 写真モードのツールバー。歩行・採寸は3Dスキャンが要るので出さない */
function createPhotoToolbar(container, { onToggleAi }) {
  return createToolbar(container, {
    onToggleMeasure: () => {},
    onToggleWalk: () => {},
    onToggleAi,
    onReset: () => {},
    only: ['ai'],
  });
}

function startViewer({ container, stage, space, property, propertyList }) {
  const { renderer, scene, overlayScene, camera, spark, render } = stage;
  const canvas = renderer.domElement;

  const rig = createCameraRig(camera, canvas);
  rig.moveTo(property.viewpoints[0], { instant: true });

  // スマホではAIパネルが下からのシートになるため、開閉に合わせて
  // 部屋切り替えボタンの位置をずらす（CSS側で #app.is-ai-open を見ている）
  const setAiVisible = (visible) => {
    ai.setVisible(visible);
    container.classList.toggle('is-ai-open', visible);
  };

  const ai = createAiPanel(container, property, {
    onClose: () => {
      setAiVisible(false);
      toolbar.setAiActive(false);
    },
  });

  const measure = createMeasureTool({
    scene: overlayScene,
    camera,
    canvas,
    space,
    onChange: (state) => toolbar.setMeasureReadout(state),
  });

  // --- 歩行モード（三人称・キンクマハムスター） ---
  // CG の物件は部屋側にライトがあるので、キャラのライトは付けない（二重に照らして白飛びする）
  const avatar = createAvatar({ lights: property.plan === null });
  avatar.root.visible = false;
  scene.add(avatar.root);

  const walk = createWalkMode({ camera, space, avatar, canvas });
  const mapCamera = createMapCamera({ camera, canvas });
  const setScanCut = createScanCut(space.object);
  /** 表示モード（scan / simple / map）と、初めて使うときに作るシンプル表示 */
  let mode = 'scan';
  let simpleMap = null;
  // マップ表示では、今いる床より上（天井・上の階）を切り取って部屋の中を見せる
  renderer.localClippingEnabled = true;

  const joystick = createJoystick(container, (x, y) => walk.setStick(x, y));

  // ズーム: マウスホイールと画面右のスライダー。
  // 見回しでは視野角、歩行ではカメラとキャラの距離を変える（両方に常に反映しておく）
  const zoom = createZoomControl(container, {
    canvas,
    onChange: (value) => {
      // マップ表示は視野角を固定し、高さでズームする
      if (mode !== 'map') {
        camera.fov = fovFor(value);
        camera.updateProjectionMatrix();
      }
      walk.setDistanceScale(distanceScaleFor(value));
      walk.setOverheadHeight(mapHeightFor(value));
      mapCamera.setHeight(mapHeightFor(value));
    },
  });

  /** 当たり判定マップは重いので、初めて歩くときに1回だけ作る */
  let navBuilt = false;
  const ensureNav = ({ rebuild = false } = {}) => {
    if (navBuilt && !rebuild) return;
    navBuilt = true;
    try {
      // メッシュは面の向きが分かるので、階段や段差を上れるマップを使う
      const layered = buildLayeredNav(space, { links: property.walkLinks });
      if (layered === null) {
        // 作れなかった理由を追えるようにしておく（視点が部屋の外にある等でよく起きる）
        console.warn('[naiken-3dgs] 段差対応の当たり判定マップを作れませんでした。簡易マップに切り替えます');
      }
      walk.setNav(layered ?? buildNavGrid(space));
    } catch (error) {
      // マップが作れなくても歩行自体はレイ判定で続けられる
      console.error('[naiken-3dgs] 当たり判定マップの作成に失敗', error);
      walk.setNav(null);
    }
  };

  /**
   * 歩行モードと見回しモードは同じカメラを奪い合うので、必ず片方だけを動かす。
   * 歩行中は採寸のクリック判定も切っておく（歩きながら測ることはない）。
   */
  const setWalking = (on) => {
    if (on) ensureNav();
    walk.setEnabled(on);
    joystick.setVisible(on);
    container.classList.toggle('is-walking', on);
    mapCamera.setActive(!on && mode === 'map');
    if (on) {
      measure.setEnabled(false);
      toolbar.setMeasureActive(false);
      walk.placeAt(current);
    } else if (mode === 'map') {
      // 歩き終えた場所をそのまま見下ろし続ける
      const p = walk.position;
      mapCamera.setFocus(p.x, p.y, p.z);
    } else {
      rig.moveTo(current, { instant: true });
    }
  };

  /*
   * 表示モード。
   * - scan   … スキャンのメッシュをそのまま見る
   * - simple … スキャンから作った床・壁・家具を箱で描き直した部屋（ぐにゃぐにゃしない）
   * - map    … simple を上から見下ろす。今いる床より上は切り取る
   * 歩行・採寸の当たり判定は、どのモードでもスキャンのメッシュで行う。
   */
  const ensureSimpleMap = () => {
    if (simpleMap !== null) return simpleMap;
    ensureNav();
    const layout = walk.nav?.layout;
    if (!layout) return null;
    /*
     * 視点ごとに部屋の名前を出す。その階の床から少し上に置く。
     * CG の物件では広さも測って「リビング 23.3帖」と出す（間取り図と同じ読み方ができるように）。
     * ひとつの部屋に視点が複数あるとき（LDK のリビング・ダイニングなど）は、
     * 同じ数字が並ばないよう最初の1つにだけ広さを付ける。
     */
    const areas = space.plan === undefined ? new Map() : measureRooms(space.plan, property.viewpoints);
    const shown = new Set();
    const labels = property.viewpoints.map((viewpoint) => {
      const [x, y, z] = mapFocusFor(viewpoint);
      const area = areas.get(viewpoint.name);
      const first = area !== undefined && !shown.has(area.room);
      if (area !== undefined) shown.add(area.room);
      return { name: first ? `${viewpoint.name} ${area.jo}帖` : viewpoint.name, x, y: y + MAP.labelLift, z };
    });
    spreadLabels(labels);
    // 視点の足元には必ず床がある。スキャンで拾えていなくても CG では床を敷く。
    // 名前（「洗面所」など）は、その辺りが水回りかどうかの手がかりに使う
    const spots = property.viewpoints.map(({ name, position }) => ({
      name,
      x: position[0],
      y: position[1] - WALK.viewpointEyeHeight,
      z: position[2],
    }));
    simpleMap = createSimpleMap(layout, { labels, nav: walk.nav, spots });
    simpleMap.root.visible = false;
    scene.add(simpleMap.root);
    return simpleMap;
  };

  /** 視点の目線の高さから、その階の床の上の見下ろす地点を求める */
  const mapFocusFor = (viewpoint) => {
    const [x, y, z] = viewpoint.position;
    const standY = y - WALK.viewpointEyeHeight;
    const spot = walk.nav?.nearestWalkable(x, z, standY) ?? null;
    return spot === null ? [x, standY, z] : [spot.x, spot.y, spot.z];
  };

  const setDisplayMode = (next) => {
    if (next !== 'scan' && ensureSimpleMap() === null) {
      modeSwitch?.setSimpleAvailable(false);
      modeSwitch?.select(mode);
      toolbar.showHint('この物件のスキャンからはシンプル表示を作れませんでした');
      return;
    }
    const wasMap = mode === 'map';
    const isMap = next === 'map';
    mode = next;
    modeSwitch?.select(next);

    // マップ表示は、上から見ると家具の形と色が一番分かるスキャンそのものを、天井を切って見せる
    space.object.visible = next !== 'simple';
    setScanCut(null);
    if (simpleMap !== null) {
      simpleMap.root.visible = next !== 'scan';
      simpleMap.setBoxesVisible(next === 'simple');
      simpleMap.setCut(null);
    }

    // 上から見るときは、間取り図のように明るい下地にする（部屋の外が黒いと形が読み取りにくい）
    renderer.setClearColor(isMap ? MAP.background : RENDER.background, 1);

    walk.setView(isMap ? 'overhead' : 'follow');
    camera.fov = isMap ? MAP.fov : fovFor(zoom.value);
    camera.updateProjectionMatrix();
    rig.setSuspended(isMap);
    mapCamera.setActive(isMap && !walk.enabled);
    if (isMap) {
      // 上から見ると天井に当たるので、マップ表示では採寸しない
      measure.setEnabled(false);
      toolbar.setMeasureActive(false);
    }
    // 最初は部屋全体が見えるよう、間取りの中心を見下ろす
    if (isMap && !wasMap) mapCamera.setFocus(...simpleMap.center);
    if (!isMap && wasMap && !walk.enabled) rig.moveTo(current, { instant: true });
  };

  /*
   * 家具を消す機能。CG の物件だけで使える（スキャンは部屋全体が1つの面のかたまりで、家具だけ外せない）。
   * 家具は歩行の当たり判定にも入っているので、消したら当たり判定を作り直す。
   */
  const remover =
    property.plan === null
      ? null
      : createFurnitureRemover({
          root: space.object,
          camera,
          canvas,
          propertyId: property.id,
          onChange: ({ count }) => {
            toolbar.setRemovedCount(count);
            if (navBuilt) ensureNav({ rebuild: true });
            // 箱で描き直した表示も作り直す
            if (simpleMap !== null) {
              scene.remove(simpleMap.root);
              simpleMap.dispose();
              simpleMap = null;
              if (mode !== 'scan') setDisplayMode(mode);
            }
          },
        });

  /*
   * 備品見積りのある物件では、CG の家具をクリックすると値段が出る。
   * 家具には設計データ側で sku（見積りの品目名）を付けてある。
   */
  const quote = property.plan === null ? null : quoteFor(property.plan);
  const priceTags =
    quote === null
      ? null
      : createPriceTags(container, { scene, root: space.object, camera, canvas, quote });

  const toolbar = createToolbar(container, {
    onToggleMeasure: (on) => {
      if (on && mode === 'map') setDisplayMode('simple');
      if (on && walk.enabled) {
        // 採寸に切り替えるときは歩行を抜ける
        setWalking(false);
        toolbar.setWalkActive(false);
      }
      measure.setEnabled(on);
    },
    onToggleWalk: (on) => setWalking(on),
    onToggleAi: (on) => setAiVisible(on),
    onReset: () => goTo(property.viewpoints[0], 0),
    ...(remover === null
      ? {}
      : {
          only: ['walk', 'measure', 'ai', 'reset', 'remove', 'restore', ...(priceTags === null ? [] : ['price'])],
          onToggleRemove: (on) => {
            // 家具を消すのと値段を見るのは、どちらもクリックを使うので同時には使えない
            if (on && priceTags?.enabled) {
              toolbar.setPriceActive(false);
              priceTags.setEnabled(false);
            }
            remover.setEnabled(on);
            toolbar.showHint(on ? '消したい家具をクリックしてください' : '');
          },
          onRestore: () => {
            remover.restoreAll();
            toolbar.showHint('家具を元に戻しました');
          },
        }),
    ...(priceTags === null
      ? {}
      : {
          onTogglePrice: (on) => {
            if (on && remover?.enabled) {
              toolbar.setRemoveActive(false);
              remover.setEnabled(false);
            }
            priceTags.setEnabled(on);
            toolbar.showHint(on ? '家具をクリックすると値段が出ます' : '');
          },
        }),
  });
  if (remover !== null) {
    toolbar.setRemovedCount(remover.count);
    remover.onRemoved((label) => toolbar.showHint(`${label}を消しました`));
  }

  // 見取りは段差を扱えるメッシュのスキャンからしか作れないので、それ以外では出さない
  const modeSwitch =
    space.kind === 'model'
      ? createDisplayModeSwitch(container, {
          initial: mode,
          onChange: (next) => {
            // 初回は見取りの作成に少し時間がかかるので、案内を描いてから作る
            if (next !== 'scan' && simpleMap === null) toolbar.showHint('マップを作成しています…');
            setTimeout(() => setDisplayMode(next), 30);
          },
        })
      : null;

  // 物件の切り替え。スキャンデータごと読み直すのでページ遷移にする。
  // ツールバーの中に並べたいので、ツールバーを作ったあとに呼ぶ。
  createPropertySwitcher(container, {
    properties: propertyList,
    currentId: property.id,
    onSelect: (id) => {
      window.location.assign(propertyPath(id, window.location.search));
    },
  });

  /** いま選ばれている視点。間取り図に印を置くときの対象になる */
  let current = property.viewpoints[0];
  /** 視点作成モードのパネル。通常の内見では作らない */
  let author = null;

  const goTo = (viewpoint, index) => {
    current = viewpoint;
    bar.select(index);
    // 歩行中は視点ジャンプではなく、その場所にキャラを立たせる
    if (walk.enabled) walk.placeAt(viewpoint);
    else if (mode === 'map') mapCamera.setFocus(...mapFocusFor(viewpoint));
    else rig.moveTo(viewpoint);
    ai.setRoom(viewpoint.name);
    floorPlan?.setActive(viewpoint.name);
    measure.clear();
  };

  const bar = createViewpointBar(container, property.viewpoints, goTo);

  const editMode = isEditMode();

  const floorPlan =
    property.floorplan === null
      ? null
      : createFloorPlan(container, {
          floorplan: property.floorplan,
          viewpoints: property.viewpoints,
          onSelect: goTo,
          editable: editMode,
          onPlace: ({ x, y }) => {
            floorPlan.setMark(current.name, { x, y });
            author?.setFloorplan(floorPlan.toJSON());
          },
        });
  floorPlan?.setActive(current.name);
  const floorPlanForward = new THREE.Vector3();

  // 実際にドラッグ／ホイール操作をしたときだけ視点移動を打ち切る。
  // 押しただけで打ち切ると、採寸のクリックで移動が途中終了してしまう。
  bindManualTakeover(canvas, () => rig.cancelFlight());

  // ?edit=1 のときだけ、社内スタッフ向けの視点作成モードを出す。
  // 内部状態の露出も調整作業に必要なこのモードに限定する。
  if (editMode) {
    author = createAuthorPanel(container, { camera, property });
    const furnitureEditor = createFurnitureEditor(container, { scene, camera, canvas, space, property });
    if (floorPlan !== null) author.setFloorplan(floorPlan.toJSON());
    window.__naiken = { property, space, camera, rig, measure, floorPlan, furnitureEditor, remover, walk, avatar, stage, setDisplayMode, getSimpleMap: () => simpleMap };
  }

  let lastFrameAt = performance.now();
  renderer.setAnimationLoop(() => {
    const now = performance.now();
    // タブを離れて戻ったときに巨大な差分が入らないよう上限を設ける
    const deltaTime = Math.min((now - lastFrameAt) / 1000, 0.1);
    lastFrameAt = now;

    if (walk.enabled) {
      const { moving, cameraDistance } = walk.update(deltaTime);
      avatar.update(deltaTime, { moving });
      // カメラが近いほどキャラを薄くして、狭い場所でも部屋が見えるようにする
      const fade = (cameraDistance - WALK.fadeNear) / (WALK.fadeFar - WALK.fadeNear);
      avatar.setOpacity(Math.min(1, Math.max(WALK.fadeMinOpacity, fade)));
    } else {
      rig.update();
      if (mode === 'map') mapCamera.update();
    }
    if (mode !== 'scan') simpleMap?.updateLabels(camera);
    priceTags?.updateTags(camera);
    if (mode === 'map') {
      const floorY = walk.enabled ? walk.position.y : mapCamera.focus.y;
      setScanCut(floorY + MAP.cutHeight);
    }

    if (floorPlan !== null) {
      camera.getWorldDirection(floorPlanForward);
      // 階の判定に使うので、y は足元（床）の高さにそろえて渡す
      const here = walk.enabled ? walk.position : camera.position;
      const floorY = walk.enabled ? here.y : here.y - WALK.viewpointEyeHeight;
      floorPlan.setPosition({ x: here.x, y: floorY, z: here.z }, floorPlanForward);
    }

    spark.update({ scene, camera });
    render();
  });
}

/**
 * スキャンの材質に「この高さより上を描かない」切り取りを付ける。
 * 戻り値の関数に高さを渡すと切り取り位置が変わる（null で切らない）。
 */
/**
 * 近くにある部屋の見出しを、上下にずらして重ならないようにする。
 *
 * 上から見ると、隣どうしの部屋（洗面所と浴室など）の見出しが同じ場所に重なって読めない。
 * 見出しは距離で大きさが変わらないので、高さをずらすと画面の上下にずれて重なりが解ける。
 */
function spreadLabels(labels) {
  /** これだけ近いと見出しが重なる(m) */
  const NEAR = 2.4;
  /** 1段ずらすごとに持ち上げる高さ(m) */
  const STEP = 0.7;
  /** 近いもの同士で同じ段を使わないよう、使った段を覚えておく */
  const levels = new Map();
  for (const [i, label] of labels.entries()) {
    // 上から見ると階の違いは重なりを防いでくれないので、平面で見た近さだけで判断する
    const taken = new Set();
    for (const other of labels.slice(0, i)) {
      if (Math.hypot(other.x - label.x, other.z - label.z) <= NEAR) taken.add(levels.get(other));
    }
    let level = 0;
    while (taken.has(level)) level += 1;
    levels.set(label, level);
    label.y += level * STEP;
  }
}

function createScanCut(root) {
  const NO_CUT = 1e6;
  const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), NO_CUT);
  root.traverse((node) => {
    if (!node.isMesh) return;
    for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
      material.clippingPlanes = [plane];
      material.needsUpdate = true;
    }
  });
  return (y) => {
    plane.constant = y === null ? NO_CUT : y;
  };
}

/** ドラッグ（一定距離以上）とホイールを「手動操作に切り替えた」と見なす */
function bindManualTakeover(canvas, onTakeover) {
  const DRAG_THRESHOLD_PX = 6;
  let pressedAt = null;

  canvas.addEventListener('pointerdown', (event) => {
    pressedAt = { x: event.clientX, y: event.clientY };
  }, { passive: true });

  canvas.addEventListener('pointermove', (event) => {
    if (pressedAt === null) return;
    const moved = Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y);
    if (moved >= DRAG_THRESHOLD_PX) {
      pressedAt = null;
      onTakeover();
    }
  }, { passive: true });

  const release = () => {
    pressedAt = null;
  };
  canvas.addEventListener('pointerup', release, { passive: true });
  canvas.addEventListener('pointercancel', release, { passive: true });
  canvas.addEventListener('wheel', onTakeover, { passive: true });
}

function isEditMode() {
  return new URLSearchParams(window.location.search).get('edit') === '1';
}

void main();
